import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Stack,
  TableCell,
  TableRow,
  Typography,
} from '@mui/material';
import { SquarePenIcon, Trash2Icon } from 'lucide-react';
import { useState } from 'react';
import { County } from '../../types/County.type';
import { Team } from '../../types/Team/Team.type';
import { TeamType } from '../../types/Team/TeamType.type';
import EditTeamModal from './EditTeamModal';

interface TeamRowProps {
  team: Team;
  counties: County[];
  teamTypes: TeamType[];
  onUpdate: (team: Team, newName: string, newCountyId: number, newTeamTypeId: number, newDatasetIds: number[]) => void;
  onDelete: (teamId: number) => void;
  isLoading: boolean;
}

function TeamRow({ team, counties, teamTypes, onUpdate, onDelete, isLoading }: TeamRowProps) {
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);

  const handleDeleteClick = () => {
    setDeleteDialogOpen(true);
  };

  const handleDeleteClose = () => {
    setDeleteDialogOpen(false);
  };

  const handleDeleteConfirm = () => {
    onDelete(team.id);
    setDeleteDialogOpen(false);
  };

  const handleEditClick = () => {
    setEditModalOpen(true);
  };

  const handleEditClose = () => {
    setEditModalOpen(false);
  };

  const handleUpdateSubmit = (newName: string, newCountyId: number, newTeamTypeId: number, newDatasetIds: number[]) => {
    onUpdate(team, newName, newCountyId, newTeamTypeId, newDatasetIds);
    setEditModalOpen(false);
  };

  return (
    <TableRow key={team.id} hover>
      <TableCell width={340}>
        <Typography variant='body2'>{team.name}</Typography>
      </TableCell>
      <TableCell width={340}>
        <Typography variant='body2'>{team.county.name}</Typography>
      </TableCell>
      <TableCell width={340}>
        <Typography variant='body2'>{team.datasets?.map((dataset) => dataset.name).join(', ')}</Typography>
      </TableCell>
      <TableCell width={320}>
        <Typography variant='body2'>{team.team_type.name}</Typography>
      </TableCell>
      <TableCell align='right' width={150}>
        <Stack direction='row' spacing={1}>
          <IconButton onClick={handleEditClick} disabled={isLoading}>
            <SquarePenIcon size={20} />
          </IconButton>
          <IconButton onClick={handleDeleteClick} disabled={isLoading}>
            <Trash2Icon size={20} />
          </IconButton>
        </Stack>
      </TableCell>
      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={handleDeleteClose}
        aria-labelledby='delete-dialog-title'
        aria-describedby='delete-dialog-description'
      >
        <DialogTitle sx={{ fontSize: '16px' }}>Confirm Delete</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ fontSize: '14px' }}>
            {`Are you sure you want to delete team "${team.name}"?`}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteClose}>Cancel</Button>
          <Button onClick={handleDeleteConfirm} color='error'>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
      {/* Edit Modal */}
      <EditTeamModal
        open={editModalOpen}
        onClose={handleEditClose}
        currentTeamName={team.name}
        currentCountyId={team.county.id}
        currentTeamTypeId={team.team_type.id}
        currentDatasetIds={team.datasets?.map((dataset) => dataset.id)}
        counties={counties}
        teamTypes={teamTypes}
        onUpdate={handleUpdateSubmit}
        loading={isLoading}
      />
    </TableRow>
  );
}

export default TeamRow;
